import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Download, Calendar, Send, Loader, Settings, Save, BarChart2, History, Users, Trash2, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { value: '0', label: 'Lunes' },
  { value: '1', label: 'Martes' },
  { value: '2', label: 'Miércoles' },
  { value: '3', label: 'Jueves' },
  { value: '4', label: 'Viernes' },
  { value: '5', label: 'Sábado' },
  { value: '6', label: 'Domingo' },
];

export default function AdminNewsletter() {
  const [subscribers, setSubscribers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("subscribers");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    frequency: 'weekly',
    day_of_week: '0',
    day_of_month: 1,
    hour: 9,
    minute: 0,
    is_enabled: true
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subRes, setRes, histRes] = await Promise.all([
          axios.get(`${API}/newsletter/subscribers`, { headers }),
          axios.get(`${API}/newsletter/settings`, { headers }),
          axios.get(`${API}/newsletter/history`, { headers })
        ]);
        setSubscribers(subRes.data);
        setSettings(setRes.data);
        setHistory(histRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API}/newsletter/settings`, settings, { headers });
      toast.success('Configuración del planificador guardada');
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Error al guardar configuración');
    } finally {
      setSavingSettings(false);
    }
  };

  const deleteSubscriber = async (hash) => {
    if (!hash) {
      toast.error('Error: Identificador de suscriptor no encontrado');
      return;
    }
    if (!window.confirm('¿Estás seguro de que quieres eliminar este suscriptor?')) return;
    try {
      await axios.delete(`${API}/newsletter/subscribers/${hash}`, { headers });
      setSubscribers(prev => prev.filter(s => s.email_hash !== hash));
      toast.success('Suscriptor eliminado');
    } catch (err) {
      console.error('Error deleting subscriber:', err);
      toast.error('Error al eliminar suscriptor');
    }
  };

  const exportCSV = () => {
    const csv = [
      'Email,Fecha de suscripción',
      ...subscribers.map(s => `${s.email},${s.subscribed_at}`)
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'suscriptores_newsletter.csv';
    a.click();
  };

  const sendNewsletter = async () => {
    if (subscribers.length === 0) {
      toast.error('No hay suscriptores para enviar');
      return;
    }
    
    setSending(true);
    try {
      const res = await axios.post(`${API}/newsletter/send`, {}, { headers });
      toast.success(`📧 ${res.data.message}`);
    } catch (err) {
      console.error('Error sending newsletter:', err);
      toast.error('Error al enviar newsletter');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "d MMM yyyy, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-semibold text-primary" data-testid="admin-newsletter-title">
            Newsletter
          </h1>
          <p className="text-muted-foreground">
            {subscribers.length} suscriptores activos
          </p>
        </div>

        <div className="flex gap-2">
          <Button onClick={sendNewsletter} disabled={sending || subscribers.length === 0} className="btn-primary" title="Enviar newsletter a todos los suscriptores">
            {sending ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Enviar ahora
              </>
            )}
          </Button>
          <Button onClick={exportCSV} className="btn-secondary" data-testid="export-csv">
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      <Tabs defaultValue="subscribers" className="w-full">
        <TabsList className="mb-8 bg-slate-100 p-1">
          <TabsTrigger value="subscribers" className="flex items-center gap-2">
            <Users className="w-4 h-4" /> Suscriptores
          </TabsTrigger>
          <TabsTrigger value="planner" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Planificador
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" /> Historial y Métricas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="subscribers">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden min-h-[400px]">
            <div className="p-4 border-b bg-slate-50/50 flex items-center gap-4">
               <div className="relative flex-1 max-w-sm">
                 <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                 <Input 
                   placeholder="Buscar por email..." 
                   className="pl-10"
                   value={searchTerm}
                   onChange={(e) => setSearchTerm(e.target.value)}
                 />
               </div>
               {subscribers.length > 0 && (
                 <Button 
                   variant="outline" 
                   size="sm" 
                   className="text-red-600 border-red-200 hover:bg-red-50"
                   onClick={() => {
                     if(window.confirm("¿BORRAR TODO? Esta acción es irreversible y eliminará a todos los suscriptores.")) {
                       // Implementamos borrado masivo vía backend si fuera necesario, 
                       // por ahora borramos uno a uno los visibles por seguridad
                       subscribers.forEach(s => deleteSubscriber(s.email_hash));
                     }
                   }}
                 >
                   <Trash2 className="w-4 h-4 mr-2" />
                   Limpiar todo
                 </Button>
               )}
            </div>

            {loading ? (
              <div className="p-8 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="skeleton h-12 rounded-lg" />
                ))}
            </div>
            ) : subscribers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="subscribers-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-4 font-medium text-muted-foreground">#</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Email</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Destinos / Tags</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Fecha suscripción</th>
                      <th className="text-right p-4 font-medium text-muted-foreground">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers
                      .filter(s => s.email.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map((subscriber, index) => (
                      <tr key={subscriber.email_hash || index} className="border-t hover:bg-slate-50 transition-colors">
                        <td className="p-4 text-muted-foreground">{index + 1}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-secondary" />
                            <span className="font-medium text-primary">{subscriber.email}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1">
                            {subscriber.tags && subscriber.tags.length > 0 ? (
                              subscriber.tags.map(tag => (
                                <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary/10 text-secondary">
                                  <Tag className="w-3 h-3 mr-1" />
                                  {tag.replace('guia_', '')}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground italic">Sin tags</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-muted-foreground text-sm">
                          {formatDate(subscriber.subscribed_at)}
                        </td>
                        <td className="p-4 text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => deleteSubscriber(subscriber.email_hash)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-20 text-center">
                <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-slate-300" />
                </div>
                <p className="text-muted-foreground italic">No hay suscriptores todavía</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="planner">
          <div className="max-w-2xl">
            <div className="bg-white rounded-xl shadow-sm p-8 border border-slate-100">
              <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-100">
                <Settings className="w-6 h-6 text-secondary" />
                <h2 className="font-serif text-2xl text-primary font-semibold">Configuración de Envío Automático</h2>
              </div>
              
              <div className="space-y-8">
                <div className="space-y-4">
                  <Label className="text-base font-semibold">Frecuencia de envío</Label>
                  <Select 
                    value={settings.frequency} 
                    onValueChange={(val) => setSettings({...settings, frequency: val})}
                  >
                    <SelectTrigger className="w-full h-11">
                      <SelectValue placeholder="Selecciona frecuencia" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Diario (Todos los días)</SelectItem>
                      <SelectItem value="weekly">Semanal (Un día a la semana)</SelectItem>
                      <SelectItem value="quincenal">Quincenal (Días 1 y 15)</SelectItem>
                      <SelectItem value="monthly">Mensual (Un día al mes)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {settings.frequency === 'weekly' && (
                    <div className="space-y-2">
                      <Label className="text-base">Día de la semana</Label>
                      <Select 
                        value={settings.day_of_week} 
                        onValueChange={(val) => setSettings({...settings, day_of_week: val})}
                      >
                        <SelectTrigger className="w-full h-11">
                          <SelectValue placeholder="Selecciona un día" />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS.map(day => (
                            <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {settings.frequency === 'monthly' && (
                    <div className="space-y-2">
                      <Label className="text-base">Día del mes (1-28)</Label>
                      <Input 
                        type="number" 
                        min="1" 
                        max="28" 
                        className="h-11"
                        value={settings.day_of_month}
                        onChange={(e) => setSettings({...settings, day_of_month: parseInt(e.target.value) || 1})}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-base">Hora (24h)</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        max="23" 
                        className="h-11"
                        value={settings.hour}
                        onChange={(e) => setSettings({...settings, hour: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-base">Minuto</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        max="59" 
                        className="h-11"
                        value={settings.minute}
                        onChange={(e) => setSettings({...settings, minute: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-secondary/5 rounded-lg border border-secondary/10">
                  <div className="flex items-center h-5">
                    <input 
                      type="checkbox" 
                      id="is_enabled_full" 
                      className="w-5 h-5 rounded border-slate-300 text-secondary focus:ring-secondary cursor-pointer"
                      checked={settings.is_enabled}
                      onChange={(e) => setSettings({...settings, is_enabled: e.target.checked})}
                    />
                  </div>
                  <Label htmlFor="is_enabled_full" className="cursor-pointer text-base font-medium">
                    Activar programador de envíos
                  </Label>
                </div>

                <Button 
                  onClick={saveSettings} 
                  className="w-full h-12 text-lg btn-secondary shadow-lg shadow-secondary/20" 
                  disabled={savingSettings}
                >
                  {savingSettings ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      Guardar Configuración
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden min-h-[400px]">
            {loading ? (
              <div className="p-8 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="skeleton h-12 rounded-lg" />
                ))}
              </div>
            ) : history.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-4 font-medium text-muted-foreground">Fecha Envío</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Asunto</th>
                      <th className="text-center p-4 font-medium text-muted-foreground">Enviados</th>
                      <th className="text-center p-4 font-medium text-muted-foreground">Leídos</th>
                      <th className="text-center p-4 font-medium text-muted-foreground">Clics</th>
                      <th className="text-center p-4 font-medium text-muted-foreground">Rendimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((campaign, index) => {
                      const openRate = campaign.recipient_count > 0 
                        ? ((campaign.read_count / campaign.recipient_count) * 100).toFixed(1) 
                        : 0;
                      return (
                        <tr key={campaign.id || index} className="border-t hover:bg-slate-50/50 transition-colors">
                          <td className="p-4 text-sm font-medium">{formatDate(campaign.sent_at)}</td>
                          <td className="p-4 text-sm">{campaign.subject}</td>
                          <td className="p-4 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {campaign.recipient_count}
                            </span>
                          </td>
                          <td className="p-4 text-center text-sm font-semibold text-green-600">
                            {campaign.read_count}
                          </td>
                          <td className="p-4 text-center text-sm font-semibold text-orange-600">
                            {campaign.click_count}
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-12 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                <div 
                                  className="bg-green-500 h-full" 
                                  style={{ width: `${Math.min(openRate, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs font-bold text-slate-600">{openRate}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-20 text-center">
                <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart2 className="w-8 h-8 text-slate-300" />
                </div>
                <p className="text-muted-foreground italic">No hay historial de envíos todavía</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
